import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoEngine } from "../context/RecoEngineContext";
import { handleRedirect, getLocationPermission, getLocation, LOCATION_PERMISSIONS } from "../../../utils/CommonFunctions";
import { ROUTES } from "../index";
import useRecoEngineSession from "./useRecoEngineSession";
import useLocationFetching from "./useLocationFetching";
import { useModal } from "../../../context/ModalContext";

const useRecoEngineBlock = () => {
  const { queryObject, setQueryObject, setLocationData, setShowLocationModal } = useRecoEngine();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { updateSessionData } = useRecoEngineSession();
  const { fetchLocationWithGeocoding } = useLocationFetching();
  const redirectingRef = useRef(false);

  const areCoordinatesSimilar = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    // Check if user has moved more than 1km from the previous location
    const latDiff = Math.abs(lat1 - lat2);
    const lonDiff = Math.abs(lon1 - lon2);
    return latDiff < 0.01 && lonDiff < 0.01;
  };
  
  const openLocationPageModal = useCallback(() => {
    openModal();
    setShowLocationModal(true); // Set showLocationModal to true when opening
  }, [openModal, setShowLocationModal]);

  const handleLocationRedirect = (
    route: string, 
    locationFSA: string, 
    inputQuery: string, 
    pageTitle: string
  ) => {
    if (redirectingRef.current) {
      console.warn("Attempted to start a second redirect, aborting.");
      return; // <-- BLOCK SECOND REDIRECT
    }
    
    redirectingRef.current = true;

    handleRedirect(
      {
        type: "IN_APP_BROWSER", 
        url: `${window.location.origin}${route}`,
        hideToolBar: true,
        traverseWebHistoryOnBackPressed: true
      },
      navigate,
      pageTitle,
      "RECO_ENGINE",
      { 
        query: inputQuery,
        fsa: locationFSA
      }
    );
  }

  const handleRecommendation = useCallback(async (inputQuery: string) => {
    redirectingRef.current = false;
    if (!inputQuery.trim()) return;

    // Store query in session for persistence across navigation
    localStorage.setItem("query", inputQuery); 
    localStorage.setItem("queryObject", JSON.stringify(queryObject));

    // Check if we have location data
    const permissionState = await getLocationPermission();
    const savedLocation = localStorage.getItem("location");
    const hasStoredLocation = savedLocation && Object.keys(JSON.parse(savedLocation || "{}")).length > 0;
    const storedLocationData = hasStoredLocation ? JSON.parse(savedLocation) : null;
      
    if (hasStoredLocation) {
      if (permissionState === LOCATION_PERMISSIONS.ALWAYS || 
          permissionState === LOCATION_PERMISSIONS.WHEN_IN_USE || 
          permissionState === LOCATION_PERMISSIONS.GRANTED) {
        
        try {
          const currentLocation = await getLocation();

          if (areCoordinatesSimilar(
            currentLocation.latitude, 
            storedLocationData.latitude, 
            currentLocation.longitude, 
            storedLocationData.longitude)
          ) {
            console.log("New location is similar to stored, using stored location data");
            handleLocationRedirect(ROUTES.RecoPage.path, storedLocationData.fsa, inputQuery, "Regional Service Assistant");
          } else {
            console.log("New location is different from stored, getting new location data");
            fetchLocationWithGeocoding(
              (locationWithFSA) => {
                localStorage.setItem("location", JSON.stringify(locationWithFSA));
                localStorage.setItem("queryObject", JSON.stringify({ 
                  ...queryObject, 
                  latitude: locationWithFSA.latitude, 
                  longitude: locationWithFSA.longitude, 
                  radius: locationWithFSA.radius,
                  fsa: locationWithFSA.fsa,
                }));
  
                handleLocationRedirect(ROUTES.RecoPage.path, locationWithFSA.fsa, inputQuery, "Regional Service Assistant");
              },
              (err) => {
                // Handle geocoding error - use stored data as fallback
                console.error("Geocoding failed, using stored location:", err);
  
                handleLocationRedirect(ROUTES.RecoPage.path, storedLocationData.fsa, inputQuery, "Regional Service Assistant");
              }
            );
          }
        } catch (error) {
          // Location access failed - use stored data
          console.error("Location access failed, using stored data:", error);
          handleLocationRedirect(ROUTES.RecoPage.path, storedLocationData.fsa, inputQuery, "Regional Service Assistant");
        }
      } else if (permissionState === LOCATION_PERMISSIONS.DENIED) {
        console.log("Location permission denied, using stored data");
        handleLocationRedirect(ROUTES.RecoPage.path, storedLocationData.fsa, inputQuery, "Regional Service Assistant");
      } else {
        // Other permission states (notDetermined, restricted, unknown) - go to location page
        console.log("Permission state unclear, redirecting to location page");
        openLocationPageModal();
      } 
    } else {
        // No stored location - check permission state
        // eslint-disable-next-line no-lonely-if
        if (permissionState === LOCATION_PERMISSIONS.NOT_DETERMINED || 
            permissionState === LOCATION_PERMISSIONS.RESTRICTED ||
            permissionState === LOCATION_PERMISSIONS.PROMPT ||
            permissionState === LOCATION_PERMISSIONS.UNAVAILABLE) {
          // Send to location page with existing behavior
          openLocationPageModal();
        } else {
          // Other permission states - also go to location page
          openLocationPageModal();
        }
      }
    }, [navigate, updateSessionData, queryObject, setLocationData, fetchLocationWithGeocoding]);
  
    const updateQuery = useCallback((query: string) => {
      setQueryObject({
        ...queryObject,
        query,
      });
    }, [queryObject, setQueryObject]);
  
    return {
      queryObject,
      handleRecommendation,
      updateQuery
    };
  };
export default useRecoEngineBlock;